import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  MAX_JOB_FILE_DESCRIPTION_LENGTH,
  MAX_JOB_FILE_NAME_LENGTH,
  jobFileExtension,
  type JobFileEntry,
  type UpdateJobFileInput,
} from '@jobos/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateJobFile } from '@/hooks/useJobFiles';

// Metadata editing for a file.
//
// The name field holds the *base* name only, with the extension pinned beside
// it: the download route sets the attachment filename from what is stored here,
// so a name saved without its extension would download as a file the OS can't
// open. Making the extension unreachable is a stronger guarantee than validating
// against changing it.

interface JobFileEditDialogProps {
  jobId: string;
  /** The file to edit; null closes the dialog. */
  file: JobFileEntry | null;
  onClose: () => void;
}

export function JobFileEditDialog({ jobId, file, onClose }: JobFileEditDialogProps) {
  return (
    <Dialog open={file != null} onOpenChange={(open) => !open && onClose()}>
      {file && (
        <DialogContent className="max-w-lg">
          {/* Keyed on the file so switching files rebuilds the form from scratch
              rather than carrying the previous file's edits across. */}
          <EditForm key={file.id} jobId={jobId} file={file} onClose={onClose} />
        </DialogContent>
      )}
    </Dialog>
  );
}

interface FormValues {
  baseName: string;
  description: string;
}

function EditForm({
  jobId,
  file,
  onClose,
}: {
  jobId: string;
  file: JobFileEntry;
  onClose: () => void;
}) {
  const { mutateAsync: updateFile, isPending } = useUpdateJobFile(jobId);

  const extension = jobFileExtension(file.fileName);
  const baseName = extension ? file.fileName.slice(0, -extension.length) : file.fileName;

  // The name limit applies to what gets stored, so the extension eats into the
  // budget the field has left.
  const maxBaseName = MAX_JOB_FILE_NAME_LENGTH - extension.length;

  const schema = useMemo(
    () =>
      z.object({
        baseName: z
          .string()
          .trim()
          .min(1, 'Enter a file name.')
          .max(maxBaseName, `That name is too long — keep it under ${maxBaseName} characters.`),
        description: z
          .string()
          .max(
            MAX_JOB_FILE_DESCRIPTION_LENGTH,
            `Keep the description under ${MAX_JOB_FILE_DESCRIPTION_LENGTH} characters.`,
          ),
      }),
    [maxBaseName],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      baseName,
      description: file.description ?? '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    const patch: UpdateJobFileInput = {};

    const fileName = `${values.baseName.trim()}${extension}`;
    if (fileName !== file.fileName) patch.fileName = fileName;

    // An emptied box means "no description", which the column stores as null —
    // an empty string would read as a description that happens to say nothing.
    const description = values.description.trim() || null;
    if (description !== (file.description ?? null)) patch.description = description;

    // Nothing to say: close rather than spend a round trip confirming it.
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    try {
      await updateFile({ id: file.id, input: patch });
      onClose();
    } catch {
      // The hook has already toasted. Staying open keeps the edit on screen so
      // it can be retried instead of retyped.
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogHeader>
        <DialogTitle>Edit file</DialogTitle>
        <DialogDescription>
          Renaming changes the display name only — the original upload is untouched.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div>
          <label htmlFor="job-file-name" className="mb-1 block text-sm font-medium text-gray-700">
            Name
          </label>
          <div className="flex items-center gap-2">
            <Input id="job-file-name" {...register('baseName')} className="flex-1" />
            {extension && (
              <span className="shrink-0 text-sm text-gray-500" aria-hidden="true">
                {extension}
              </span>
            )}
          </div>
          {extension && (
            <p className="mt-1 text-xs text-gray-500">
              The {extension} extension stays as it is.
            </p>
          )}
          {errors.baseName && (
            <p className="mt-1 text-xs text-red-600">{errors.baseName.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="job-file-description"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Description
          </label>
          <Textarea
            id="job-file-description"
            rows={3}
            placeholder="What this file shows, if it isn't obvious from the name"
            {...register('description')}
          />
          {errors.description && (
            <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
